// src/components/Dashboard/LayerTags.tsx
import React, { useState, useEffect, useRef } from 'react';
import type { Layer } from '../../types/dashboard';
import { apiService } from '../../services/api';
import { STORAGE_KEYS } from '../../lib/constants';

interface LayerTagsProps {
  layers: Layer[];
}

interface ContextMenu {
  x: number;
  y: number;
  layer: Layer;
}

export const LayerTags: React.FC<LayerTagsProps> = ({ layers }) =>
{
  const [orderedLayers, setOrderedLayers] = useState<Layer[]>([]);
  const [contextMenu, setContextMenu] = useState<ContextMenu | null>(null);
  const [hiddenLayers, setHiddenLayers] = useState<number[]>([]);
  const menuRef = useRef<HTMLDivElement>(null);

  // Cargar layers ocultos del localStorage
  useEffect(() =>
  {
    const stored = localStorage.getItem(STORAGE_KEYS.HIDDEN_LAYERS);
    if (stored)
    {
      try
      {
        setHiddenLayers(JSON.parse(stored));
      }
      catch (e)
      {
        console.error('Error parsing hiddenLayers:', e);
      }
    }
  }, []);

  // Función para ordenar los layers según el layerId de localStorage
  const sortLayers = (layersList: Layer[], selectedLayerId: number | null) =>
  {
    if (!selectedLayerId) return layersList;

    const selectedLayer = layersList.find(l => l.LayerId === selectedLayerId);
    if (!selectedLayer) return layersList;

    const otherLayers = layersList.filter(l => l.LayerId !== selectedLayerId);
    return [selectedLayer, ...otherLayers];
  };

  // Ordenar layers al montar el componente o cuando cambien
  useEffect(() =>
  {
    const layerId = Number(localStorage.getItem(STORAGE_KEYS.LAYERID));
    const sorted = sortLayers(layers, layerId);
    setOrderedLayers(sorted);
  }, [layers]);

  // Manejar click simple - abre el context menu
  const handleClick = (e: React.MouseEvent, layer: Layer) =>
  {
    e.preventDefault();
    e.stopPropagation();

    const rect = e.currentTarget.getBoundingClientRect();

    setContextMenu({
      x: rect.right - 120, // Alinear al borde derecho del elemento
      y: rect.bottom,
      layer,
    });
  };

  // Cerrar menú al hacer click fuera
  useEffect(() =>
  {
    const handleClickOutside = (e: MouseEvent) =>
    {
      if (menuRef.current && !menuRef.current.contains(e.target as Node))
      {
        setContextMenu(null);
      }
    };

    if (contextMenu)
    {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () =>
    {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [contextMenu]);

  // Toggle hide/show layer
  const handleToggleHide = (layerId: number) =>
  {
    const newHiddenLayers = hiddenLayers.includes(layerId)
      ? hiddenLayers.filter(id => id !== layerId)
      : [...hiddenLayers, layerId];

    setHiddenLayers(newHiddenLayers);
    localStorage.setItem(STORAGE_KEYS.HIDDEN_LAYERS, JSON.stringify(newHiddenLayers));
    sessionStorage.setItem('C2_LayersToHide', JSON.stringify(newHiddenLayers));

    // Disparar evento para que las secciones recarguen datos
    window.dispatchEvent(
      new CustomEvent('layersToHideChanged', {
        detail: { hiddenLayers: newHiddenLayers },
      }),
    );

    setContextMenu(null);
  };

  // Make active layer - calls API and updates localStorage
  const handleMakeActive = async (layer: Layer) =>
  {
    try
    {
      await apiService.post('/person/UpdateDefaultLayer', { layerId: layer.LayerId });
      localStorage.setItem(STORAGE_KEYS.LAYERID, String(layer.LayerId));
      window.dispatchEvent(new CustomEvent('layerChanged', { detail: { layerId: layer.LayerId } }));
      const sorted = sortLayers(layers, layer.LayerId);
      setOrderedLayers(sorted);
    }
    catch (error)
    {
      console.error('Error updating default layer:', error);
    }
    setContextMenu(null);
  };

  // Verificar si un layer está oculto
  const isLayerHidden = (layerId: number) => hiddenLayers.includes(layerId);

  return (
    <div className="relative">
      <div className="layer-tags-table">
        {orderedLayers.map((layer, index) =>
        {
          const isHidden = isLayerHidden(layer.LayerId);

          return (
            <div
              key={layer.LayerId}
              onClick={e => handleClick(e, layer)}
              onDoubleClick={e =>
              {
                e.preventDefault();
                e.stopPropagation();
                setContextMenu(null);
                handleMakeActive(layer);
              }}
              className={
                index === 0
                  ? 'table-cell align-top text-right relative whitespace-nowrap text-[9px] leading-[1.43] text-[#414042] font-sans w-[9999px] border-0 box-border bg-[rgb(255,201,137)] scrollbar-thin scrollbar-thumb-[#ccc] scrollbar-track-[#f1f1f1] py-[2px] px-[8px] text-black/50 tracking-[1px] text-[10px] font-normal uppercase cursor-pointer'
                  : 'table-cell align-top text-right relative whitespace-nowrap text-[9px] leading-[1.43] text-[#414042] font-sans border-l border-l-white box-border scrollbar-thin scrollbar-thumb-[#ccc] scrollbar-track-[#f1f1f1] py-[2px] px-[8px] text-black/50 tracking-[1px] text-[10px] font-normal uppercase cursor-pointer'
              }
              style={{
                backgroundColor: isHidden ? 'rgb(204, 204, 204)' : layer.Color,
                color: layer.TextColor ? '#000000' : '#FFFFFF',
              }}
              title={`Layer: ${layer.Name}`}
            >
              {layer.Name}
            </div>
          );
        })}
      </div>

      {/* Context Menu - styled like legacy custom-menu user-layers */}
      {contextMenu && (
        <div
          ref={menuRef}
          className="fixed z-[9999] w-[120px] h-[60px]"
          style={{
            left: `${contextMenu.x}px`,
            top: `${contextMenu.y}px`,
          }}
        >
          <div
            className="bg-white shadow-lg rounded-b"
            style={{
              borderStyle: 'solid',
              borderTopWidth: '10px',
              borderTopColor: contextMenu.layer.Color,
            }}
          >
            <ul className="list-none m-0 p-0">
              <li>
                <button
                  onClick={() => handleToggleHide(contextMenu.layer.LayerId)}
                  className="w-full text-left px-2 py-2 text-[9px] hover:bg-gray-100 text-gray-700 flex items-center gap-2 cursor-pointer"
                >
                  <span className="icon-eye-icon text-[9px]"></span>
                  <span>
                    {isLayerHidden(contextMenu.layer.LayerId) ? 'Show Content' : 'Hide Content'}
                  </span>
                </button>
              </li>
              <li>
                <button
                  onClick={() => handleMakeActive(contextMenu.layer)}
                  className="w-full text-left px-2 py-2 text-[9px] hover:bg-gray-100 text-gray-700 flex items-center gap-2 cursor-pointer"
                >
                  <span>
                    <i
                      className="material-icons"
                      style={{ fontSize: '9px', verticalAlign: 'middle' }}
                    >
                      wb_iridescent
                    </i>
                  </span>
                  <span>Make Active Layer</span>
                </button>
              </li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};
